/**
 * Error translation utility to convert backend/database errors to user-friendly German messages
 */

// Map of common backend errors to German translations
const errorTranslations: Record<string, string> = {
	// Date/Time errors
	"date/time field value out of range":
		"Das Datum liegt außerhalb des gültigen Bereichs. Bitte geben Sie ein gültiges Datum ein.",
	"invalid input syntax for type date":
		"Ungültiges Datumsformat. Bitte verwenden Sie das Format TT.MM.JJJJ.",
	"date/time field value out of range:":
		"Das Datum liegt außerhalb des gültigen Bereichs. Bitte geben Sie ein gültiges Datum ein.",

	// Network errors
	"Network Error":
		"Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.",
	"Request failed with status code 500":
		"Serverfehler. Bitte versuchen Sie es später erneut.",
	"Request failed with status code 404":
		"Die angeforderte Ressource wurde nicht gefunden.",
	"Request failed with status code 403":
		"Sie haben keine Berechtigung für diese Aktion.",
	"Request failed with status code 401":
		"Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.",
	"Request failed with status code 400":
		"Ungültige Anfrage. Bitte überprüfen Sie Ihre Eingaben.",

	// Validation errors
	"Validation failed":
		"Validierung fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.",
	"Required field missing":
		"Pflichtfeld fehlt. Bitte füllen Sie alle erforderlichen Felder aus.",
	"Invalid email format":
		"Ungültige E-Mail-Adresse. Bitte geben Sie eine gültige E-Mail-Adresse ein.",
	"Invalid phone number":
		"Ungültige Telefonnummer. Bitte geben Sie eine gültige Telefonnummer ein.",

	// Authentication errors
	"Invalid credentials":
		"Ungültige Anmeldedaten. Bitte überprüfen Sie Ihre E-Mail-Adresse und Ihr Passwort.",
	"User not found": "Benutzer nicht gefunden.",
	"Account locked":
		"Ihr Konto wurde gesperrt. Bitte kontaktieren Sie den Support.",
	"Password reset required": "Passwort-Zurücksetzung erforderliche.",

	// File upload errors
	"File too large": "Die Datei ist zu groß. Maximale Dateigröße ist 10 MB.",
	"Invalid file type":
		"Ungültiger Dateityp. Bitte laden Sie nur erlaubte Dateiformate hoch.",
	"File upload failed":
		"Datei-Upload fehlgeschlagen. Bitte versuchen Sie es erneut.",

	// Database errors
	"unique constraint":
		"Dieser Wert existiert bereits. Bitte verwenden Sie einen anderen Wert.",
	"foreign key constraint":
		"Diese Aktion kann nicht ausgeführt werden, da verknüpfte Daten existieren.",
	"check constraint":
		"Die eingegebenen Daten erfüllen nicht die erforderlichen Bedingungen.",

	// Generic errors
	"An error occurred":
		"Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
	"Operation failed":
		"Vorgang fehlgeschlagen. Bitte versuchen Sie es später erneut.",
	"Something went wrong":
		"Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",

	// Resource-specific errors
	"Failed to create Verify":
		"Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.",
	"Failed to update Verify":
		"Aktualisierung der Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.",
	"Failed to load Verify":
		"Laden der Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.",
};

/**
 * Translates a backend error message to a user-friendly German message
 * @param error - The error message or error object
 * @returns Translated German error message
 */
export function translateError(error: string | Error | unknown): string {
	let errorMessage = "";

	// Extract error message from different error formats
	if (typeof error === "string") {
		errorMessage = error;
	} else if (error instanceof Error) {
		errorMessage = error.message;
	} else if (error && typeof error === "object" && "message" in error) {
		errorMessage = String((error as any).message);
	}

	// Check for exact match first
	if (errorTranslations[errorMessage]) {
		return errorTranslations[errorMessage];
	}

	// Check for partial matches (case-insensitive)
	const lowerErrorMessage = errorMessage.toLowerCase();
	for (const [key, translation] of Object.entries(errorTranslations)) {
		if (lowerErrorMessage.includes(key.toLowerCase())) {
			return translation;
		}
	}

	// Special handling for date/time errors that might have additional context
	if (lowerErrorMessage.includes("date/time field value out of range")) {
		return "Das Datum liegt außerhalb des gültigen Bereichs. Bitte geben Sie ein gültiges Datum ein.";
	}

	return errorMessage;
}

/**
 * Checks if an error message is already in German
 * @param message - The error message to check
 * @returns true if the message appears to be in German
 */
export function isGermanError(message: string): boolean {
	// Common German words that would indicate the message is already translated
	const germanIndicators = [
		"bitte",
		"sie",
		"ihre",
		"ein",
		"ist",
		"wurde",
		"nicht",
		"oder",
		"und",
		"der",
		"die",
		"das",
		"fehler",
		"ungültig",
		"erneut",
	];

	const lowerMessage = message.toLowerCase();
	return germanIndicators.some((word) => lowerMessage.includes(word));
}
